/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { IEditor } from "../../IEditor";
import { ReadmeEditorView } from "./ReadmeEditorEdit/ReadmeEditorView";
import { CMD } from "../../commands/CMD";
import { CreateReadmeDialog } from "./CreateReadmeDialog/CreateReadmeDialog";
import { TOAST_TYPE } from "../../toasts/IToast";

export function registerCommands(dc: IEditor): void {
	const viewSvc = dc.get("viewService");
	const commandsSvc = dc.get("commandsService");
	const dialogSvc = dc.get("dialogService");
	const toastSvc = dc.get("toastService");

	commandsSvc.register({
		id: CMD.README_EDITOR.CREATE.OPEN,
		label: "Create Readme",
		args: [
			{
				label: "Base path",
				isOptional: true,
				schema: {
					type: "string"
				}
			}
		],
		handler: async (args) => {
			const basePath = args[0] as string;

			const res = await dialogSvc.open(CreateReadmeDialog, "readmeEditor.createReadme", { basePath });

			if (res.resourceId) {
				commandsSvc.execute("resource.open", res.resourceId);
			}

			return res.resourceId;
		}
	});

	commandsSvc.register({
		id: CMD.README_EDITOR.EDIT.UNDO,
		label: "Undo",
		handler: async () => {
			if (viewSvc.isViewActive("readmeEditor.edit")) {
				const view = viewSvc.getCurrentView() as ReadmeEditorView;
				view.undo();
			}
		}
	});

	commandsSvc.register({
		id: CMD.README_EDITOR.EDIT.REDO,
		label: "Redo",
		handler: async () => {
			if (viewSvc.isViewActive("readmeEditor.edit")) {
				const view = viewSvc.getCurrentView() as ReadmeEditorView;
				view.redo();
			}
		}
	});

	commandsSvc.register({
		id: CMD.README_EDITOR.EDIT.SAVE,
		label: "Save Readme",
		handler: async () => {
			if (viewSvc.isViewActive("readmeEditor.edit")) {
				const view = viewSvc.getCurrentView() as ReadmeEditorView;

				if (!view.isModified || view.isBeingSaved()) {
					return;
				}

				const loadingToast = toastSvc.add({
					type: TOAST_TYPE.LOADING,
					message: "Saving readme...",
					isClosable: false
				});

				try {
					await view.save();

					toastSvc.add({
						type: TOAST_TYPE.SUCCESS,
						message: "Readme has been saved.",
						timeoutMs: 2500
					});
				} catch (err) {

					toastSvc.add({
						type: TOAST_TYPE.ERROR,
						message: "Failed to save readme.",
						details: String(err)
					});
				} finally {
					toastSvc.remove(loadingToast);
				}
			}
		}
	});
}
